import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { RestaurantSelector } from '@/components/shared/RestaurantSelector';
import { NotificationCenter } from '@/components/alerts/NotificationCenter';
import { Settings, LogOut, Search, Package, ArrowRight, Sun, Moon, Cloud } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { SearchResult } from './types';

interface DashboardHeaderProps {
  profile: { email: string; role: string; display_name?: string } | null;
  restaurantName?: string;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isSearchFocused: boolean;
  onSearchFocus: () => void;
  onSearchBlur: () => void;
  searchResults: SearchResult[];
  onLogout: () => void;
}

function getGreeting(): { text: string; icon: React.ReactNode } {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return { text: 'Bonjour', icon: <Sun className="w-5 h-5 text-yellow-500" /> };
  } else if (hour >= 12 && hour < 18) {
    return { text: 'Bon après-midi', icon: <Cloud className="w-5 h-5 text-blue-400" /> };
  } else {
    return { text: 'Bonsoir', icon: <Moon className="w-5 h-5 text-indigo-400" /> };
  }
}

export function DashboardHeader({
  profile,
  restaurantName,
  searchQuery,
  onSearchChange,
  isSearchFocused,
  onSearchFocus,
  onSearchBlur,
  searchResults,
  onLogout,
}: DashboardHeaderProps) {
  const navigate = useNavigate();
  const greeting = getGreeting();
  const today = format(new Date(), "EEEE d MMMM yyyy", { locale: fr });
  const displayName = profile?.display_name || profile?.email?.split('@')[0] || '';

  return (
    <div className="mb-8 space-y-4">
      {/* Ligne 1: Message d'accueil + Boutons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {greeting.icon}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              {greeting.text}, {displayName}
            </h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
            {today}
            {restaurantName && (
              <span className="ml-2 text-orange-600 dark:text-orange-400 font-medium">
                • {restaurantName}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <NotificationCenter />
          <Button
            onClick={() => navigate('/settings')}
            variant="outline"
            size="sm"
            className="flex-1 sm:flex-none"
          >
            <Settings size={16} />
            <span className="hidden sm:inline ml-1">Paramètres</span>
          </Button>
          <Button
            onClick={onLogout}
            variant="outline"
            size="sm"
            className="flex-1 sm:flex-none"
          >
            <LogOut size={16} />
            <span className="hidden sm:inline ml-1">Déconnexion</span>
          </Button>
        </div>
      </div>

      {/* Ligne 2: Global Search + Restaurant Selector */}
      <div className="flex flex-col sm:flex-row gap-3 w-full">
        {/* Global Search Bar */}
        <div className="relative flex-1 sm:max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <Input
            type="text"
            placeholder="Rechercher produits, mouvements..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={onSearchFocus}
            onBlur={onSearchBlur}
            className="pl-10 h-10 w-full"
          />

          {/* Search Results Dropdown */}
          {isSearchFocused && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
              {searchResults.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:bg-gray-800 transition-colors text-left"
                  onClick={() => {
                    if (result.type === 'product') {
                      navigate('/products');
                    } else {
                      navigate('/history');
                    }
                    onSearchChange('');
                  }}
                >
                  <div
                    className={`p-1.5 rounded-lg ${
                      result.type === 'product'
                        ? 'bg-blue-100 dark:bg-blue-900/50'
                        : 'bg-green-100'
                    }`}
                  >
                    {result.icon === 'package' ? (
                      <Package size={16} className="text-blue-600" />
                    ) : (
                      <ArrowRight size={16} className="text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                      {result.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {result.subtitle}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {isSearchFocused && searchQuery.length >= 2 && searchResults.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 p-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Aucun résultat trouvé
              </p>
            </div>
          )}
        </div>

        {/* Restaurant Selector */}
        <div className="flex-1">
          <RestaurantSelector />
        </div>
      </div>
    </div>
  );
}
